import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { sanitizeRichTextHtml } from "../../components/RichTextComponent";
import { KvkkService } from "../../services/kvkkService";
import { KvkkValidator } from "../../validators/kvkkValidator";
import { getResponseErrorMessage, isNotFoundError } from "../../utils/cmsResponse";
import { getErrorMessage } from "../../utils/errorUtils";
import { toComparableRichText } from "../../utils/richText";
import { ApiResponse, OperationStatus } from "../VolunteersHighlightContext/constants";
import {
	getDefaultKvkkFormData,
	KVKK_LIST_PATH,
	KvkkDetail,
	KvkkEditFormData,
	KvkkFormData,
	KvkkGeneralFormData,
	KvkkListItem,
	toKvkkEditFormData,
	UpdateKvkkLocalizable,
	UpdateKvkkRequest,
} from "./constants";

const NOT_FOUND_MESSAGE = "KVKK metni bulunamadı.";

const ERROR_MESSAGES = {
	list: "KVKK metinleri yüklenemedi.",
	detail: "KVKK metni yüklenemedi.",
	add: "KVKK metni eklenemedi.",
	update: "KVKK metni güncellenemedi.",
	delete: "KVKK metni silinemedi.",
};

// The backend has no texts for its KVKK messages yet and sends their keys (for example "[KvkkUpdateError]"); the
// screen's own sentence is shown instead of a key.
const BACKEND_MESSAGE_KEY = /^\[\w+\]$/;

const toReadableMessage = (text: string, fallback: string) => (!text || BACKEND_MESSAGE_KEY.test(text.trim()) ? fallback : text);

interface KvkkContextState {
	// Core Domain Data
	kvkkList: KvkkListItem[];
	kvkkDetail: KvkkDetail | null;
	kvkkForm: KvkkFormData;
	kvkkEditForm: KvkkEditFormData | null;
	operationStatus: OperationStatus;

	// Setters
	setKvkkForm: React.Dispatch<React.SetStateAction<KvkkFormData>>;
	setKvkkEditForm: React.Dispatch<React.SetStateAction<KvkkEditFormData | null>>;
	clearKvkkFormData: () => void;
	clearKvkkList: () => void;

	// Actions
	getKvkkList: () => Promise<void>;
	getKvkkDetails: (id: string) => Promise<void>;
	addKvkk: () => Promise<void>;
	updateKvkk: () => Promise<void>;
	removeKvkk: (id: string) => Promise<void>;
}

interface KvkkProviderProps {
	children: React.ReactNode;
}

const KvkkContext = createContext<KvkkContextState | undefined>(undefined);

export const useKvkkContext = () => {
	const context = useContext(KvkkContext);
	if (!context) {
		throw new Error("useKvkkContext must be used within a KvkkContextProvider");
	}
	return context;
};

const showError = (error: unknown, fallback: string) => {
	console.error(error);
	message.error(isNotFoundError(error) ? NOT_FOUND_MESSAGE : toReadableMessage(getErrorMessage(error), fallback));
};

const showResponseError = (response: ApiResponse<unknown>, fallback: string) =>
	message.error(toReadableMessage(getResponseErrorMessage(response), fallback));

// The editor hands back a stored text in its own form (older texts lose their styles and headings there), which alone is
// not a change: sending it would rewrite a text the user did not touch.
const isTextChanged = (current: string, original: string) =>
	toComparableRichText(sanitizeRichTextHtml(current)) !== toComparableRichText(sanitizeRichTextHtml(original));

/* -------------- Build Update Request (Only Changed Fields) -------------- */

export const buildKvkkUpdateRequest = (current: KvkkEditFormData, original: KvkkEditFormData): UpdateKvkkRequest => {
	const request: UpdateKvkkRequest = {};

	if (current.platform !== original.platform) {
		request.platform = current.platform;
	}
	if (current.appId !== original.appId) {
		request.appId = current.appId;
	}
	if (current.isPage !== original.isPage) {
		request.isPage = current.isPage;
	}
	if (current.isManager !== original.isManager) {
		request.isManager = current.isManager;
	}

	const changedLocalizables = current.kvkkTextLocalizable.flatMap(item => {
		const before = original.kvkkTextLocalizable.find(o => o.culture === item.culture);
		const changed: UpdateKvkkLocalizable = { culture: item.culture };
		if (item.kvkkTextLocalizableId) {
			changed.kvkkTextLocalizableId = item.kvkkTextLocalizableId;
		}
		let hasChange = false;

		if (item.title.trim() !== (before?.title ?? "").trim()) {
			changed.title = item.title;
			hasChange = true;
		}
		if (isTextChanged(item.text, before?.text ?? "")) {
			changed.text = item.text;
			hasChange = true;
		}

		return hasChange ? [changed] : [];
	});

	if (changedLocalizables.length > 0) {
		request.kvkkTextLocalizable = changedLocalizables;
	}

	return request;
};

export const KvkkContextProvider: React.FC<KvkkProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [kvkkList, setKvkkList] = useState<KvkkListItem[]>([]);
	const [kvkkDetail, setKvkkDetail] = useState<KvkkDetail | null>(null);
	const [kvkkForm, setKvkkForm] = useState<KvkkFormData>(getDefaultKvkkFormData);
	const [kvkkEditForm, setKvkkEditForm] = useState<KvkkEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	// The loading state only disables the buttons after a re-render, so a fast double click could still send a
	// second request; this ref blocks overlapping add/update/delete calls synchronously.
	const isMutatingRef = useRef(false);

	/* -------------- Clear helpers -------------- */

	const clearKvkkFormData = useCallback(() => {
		setKvkkForm(getDefaultKvkkFormData());
		setKvkkEditForm(null);
		setKvkkDetail(null);
	}, []);

	const clearKvkkList = useCallback(() => {
		setKvkkList([]);
	}, []);

	// Checked against the list as it is now, so a record added in the meantime (another tab, another user) is not
	// duplicated. false when the combination is taken or the list cannot be read; the reason is shown.
	const checkUniqueness = useCallback(async (data: KvkkGeneralFormData, exceptId?: string) => {
		const response = await KvkkService.getKvkkList();
		if (!response.isSuccessful) {
			showResponseError(response, ERROR_MESSAGES.list);
			return false;
		}
		const duplicateError = KvkkValidator.validateUniqueKvkk(response.result?.items ?? [], data, exceptId);
		if (duplicateError) {
			message.info(duplicateError);
			return false;
		}
		return true;
	}, []);

	/* -------------- Actions -------------- */

	const getKvkkList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await KvkkService.getKvkkList();

			if (response.isSuccessful) {
				setKvkkList(response.result?.items ?? []);
			} else {
				showResponseError(response, ERROR_MESSAGES.list);
			}
		} catch (error) {
			showError(error, ERROR_MESSAGES.list);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getKvkkDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await KvkkService.getKvkkDetails(id);

			if (response.isSuccessful) {
				setKvkkDetail(response.result);
				setKvkkEditForm(toKvkkEditFormData(response.result));
			} else {
				showResponseError(response, ERROR_MESSAGES.detail);
			}
		} catch (error) {
			showError(error, ERROR_MESSAGES.detail);
			if (isNotFoundError(error)) {
				navigate(KVKK_LIST_PATH);
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [navigate]);

	const addKvkk = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		const validationError = KvkkValidator.validateAddKvkk(kvkkForm);
		if (validationError) {
			message.info(validationError);
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "add" });

		try {
			if (!(await checkUniqueness(kvkkForm))) {
				return;
			}

			const response = await KvkkService.addKvkk(kvkkForm);

			if (response.isSuccessful) {
				message.success("KVKK metni başarıyla eklendi.");
				setKvkkForm(getDefaultKvkkFormData());
				navigate(KVKK_LIST_PATH);
			} else {
				showResponseError(response, ERROR_MESSAGES.add);
			}
		} catch (error) {
			showError(error, ERROR_MESSAGES.add);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [kvkkForm, navigate, checkUniqueness]);

	const updateKvkk = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}
		if (!kvkkEditForm || !kvkkDetail) {
			message.error(NOT_FOUND_MESSAGE);
			return;
		}

		const updateRequest = buildKvkkUpdateRequest(kvkkEditForm, toKvkkEditFormData(kvkkDetail));

		const validationError = KvkkValidator.validateUpdateKvkk(updateRequest);
		if (validationError) {
			message.info(validationError);
			return;
		}

		if (Object.keys(updateRequest).length === 0) {
			message.info("Herhangi bir değişiklik yapılmadı.");
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "update" });

		try {
			const isKindChanged = ["platform", "appId", "isPage", "isManager"].some(key => key in updateRequest);
			if (isKindChanged && !(await checkUniqueness(kvkkEditForm, kvkkEditForm.kvkkTextId))) {
				return;
			}

			const response = await KvkkService.updateKvkk(kvkkEditForm.kvkkTextId, updateRequest);

			if (response.isSuccessful) {
				message.success("KVKK metni başarıyla güncellendi.");
				clearKvkkFormData();
				navigate(KVKK_LIST_PATH);
			} else {
				showResponseError(response, ERROR_MESSAGES.update);
			}
		} catch (error) {
			showError(error, ERROR_MESSAGES.update);
			if (isNotFoundError(error)) {
				navigate(KVKK_LIST_PATH);
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [kvkkEditForm, kvkkDetail, navigate, clearKvkkFormData, checkUniqueness]);

	const removeKvkk = useCallback(async (id: string) => {
		if (isMutatingRef.current) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await KvkkService.removeKvkk(id);

			if (response.isSuccessful) {
				message.success("KVKK metni başarıyla silindi.");
			} else {
				showResponseError(response, ERROR_MESSAGES.delete);
			}
		} catch (error) {
			showError(error, ERROR_MESSAGES.delete);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}

		// A record deleted elsewhere in the meantime (404) leaves the list too, so it is refreshed after every attempt.
		await getKvkkList();
	}, [getKvkkList]);

	const contextValue = useMemo<KvkkContextState>(
		() => ({
			kvkkList,
			kvkkDetail,
			kvkkForm,
			kvkkEditForm,
			operationStatus,
			setKvkkForm,
			setKvkkEditForm,
			clearKvkkFormData,
			clearKvkkList,
			getKvkkList,
			getKvkkDetails,
			addKvkk,
			updateKvkk,
			removeKvkk,
		}),
		[
			kvkkList,
			kvkkDetail,
			kvkkForm,
			kvkkEditForm,
			operationStatus,
			clearKvkkFormData,
			clearKvkkList,
			getKvkkList,
			getKvkkDetails,
			addKvkk,
			updateKvkk,
			removeKvkk,
		]
	);

	return <KvkkContext.Provider value={contextValue}>{children}</KvkkContext.Provider>;
};
